import React, { memo, useCallback, useMemo } from "react";
import type {
  RegistrationCardData,
  RegistrationCardVariant,
} from "@/types/cards";
import { cn } from "@/lib/utils";
import {
  ApprovePlayerIconCancel,
  ApprovePlayerIconCheck,
  ApprovePlayerIconSend,
  ApprovePlayerUserRoundX,
  Button,
  Avatar,
  AvatarImage,
  AvatarFallback,
  DefaultAvatarFilled,
} from "@mebi/ui";

/**
 * RegistrationCard component renders two variants based on the active tab:
 * - variant="info": ApprovePlayer/InfoCard for tab "Hồ sơ"
 * - variant="fee":  ApprovePlayer/FeeCard for tab "Lệ phí"
 *
 * It displays timestamp, status chip, avatar + name, and variant-specific content.
 * Buttons expose event callbacks: onApprove, onReject, onRequestResend.
 */

/** Small rounded status indicator following Figma colors */
const STATUS_CHIP_CLASSES = {
  yellow: {
    bg: "bg-Color-Background-brand-inverted",
    fg: "text-Color-Text-brand-default",
  },
  green: {
    bg: "bg-Color-Background-positive-inverted",
    fg: "text-Color-Text-semantic-positive",
  },
  red: {
    bg: "bg-Color-Background-negative-inverted",
    fg: "text-Color-Text-semantic-negative",
  },
} as const;

const StatusChip: React.FC<{
  text: string;
  color?: "yellow" | "green" | "red";
}> = memo(({ text, color = "yellow" }) => {
  const chipClass = STATUS_CHIP_CLASSES[color];
  return (
    <span
      className={cn(
        "px-3 py-1 rounded-full text-[10px] font-semibold",
        chipClass.bg,
        chipClass.fg,
      )}
    >
      {text}
    </span>
  );
});

const getTimestampIcon = (
  variant: RegistrationCardVariant,
  status: RegistrationCardData["status"],
) => {
  if (variant === "info") {
    if (status === "CANCELLED_BY_PLAYER") {
      return (
        <ApprovePlayerUserRoundX
          className="w-4 h-4"
          data-testid="icon-cancelled"
        />
      );
    }
    if (status === "REJECTED") {
      return (
        <ApprovePlayerIconCancel
          className="w-4 h-4"
          data-testid="icon-rejected"
        />
      );
    }
    if (status === "PROFILE_APPROVED" || status === "PENDING_PAYMENT") {
      return (
        <ApprovePlayerIconCheck
          className="w-4 h-4"
          data-testid="icon-approved"
        />
      );
    }
    return (
      <ApprovePlayerIconSend className="w-4 h-4" data-testid="icon-pending" />
    );
  }

  if (status === "PAYMENT_CONFIRMED") {
    return (
      <ApprovePlayerIconCheck className="w-4 h-4" data-testid="icon-approved" />
    );
  }
  return (
    <ApprovePlayerIconSend className="w-4 h-4" data-testid="icon-pending" />
  );
};

const getTimestampClassName = (isApproved: boolean, isRejected: boolean) => {
  if (isApproved) return "text-Color-Text-semantic-positive";
  if (isRejected) return "text-Color-Text-semantic-negative";
  return "text-Color-Text-neutral-supplement";
};

const InfoSection: React.FC<{
  sequenceNumber?: number;
  playerName: string;
  playerFullName?: string;
  age?: number;
  onApprove?: () => void;
  onReject?: () => void;
}> = memo(
  ({
    sequenceNumber,
    playerName,
    playerFullName,
    age,
    onApprove,
    onReject,
  }) => {
    const handleReject = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onReject?.();
      },
      [onReject],
    );

    const handleApprove = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onApprove?.();
      },
      [onApprove],
    );

    return (
      <>
        <div className="flex flex-col gap-2 w-full mt-3">
          <div className="flex items-start justify-between">
            <span className="text-[14px] text-Color-Text-neutral-supplement max-w-[100px]">
              STT
            </span>
            <span className="text-[14px] text-Color-Text-neutral-black">
              {typeof sequenceNumber === "number"
                ? String(sequenceNumber).padStart(2, "0")
                : "—"}
            </span>
          </div>
          <div className="flex items-start justify-between">
            <span className="text-[14px] text-Color-Text-neutral-supplement max-w-[100px]">
              Tên
            </span>
            <span className="text-[14px] text-Color-Text-neutral-black">
              {playerFullName || playerName}
            </span>
          </div>
          <div className="flex items-start justify-between">
            <span className="text-[14px] text-Color-Text-neutral-supplement max-w-[100px]">
              Tuổi
            </span>
            <span className="text-[14px] text-Color-Text-neutral-black">
              {typeof age === "number" ? age : "…"}
            </span>
          </div>
        </div>

        {(onReject || onApprove) && (
          <div className="flex items-center gap-4 mt-1 w-full">
            {onReject && (
              <Button
                size="sm"
                variant="ghost"
                className="flex-1 h-6 px-2 rounded bg-Color-Background-neutral-default_primary border border-Color-Stroke-neutral text-xs leading-4"
                onClick={handleReject}
              >
                Từ chối
              </Button>
            )}
            {onApprove && (
              <Button
                size="sm"
                variant="primary"
                className="flex-1 h-6 px-2 rounded text-xs leading-4"
                onClick={handleApprove}
              >
                Duyệt
              </Button>
            )}
          </div>
        )}
      </>
    );
  },
);

const FeeSection: React.FC<{
  paymentProofUrl?: string;
  onApprove?: () => void;
  onRequestResend?: () => void;
}> = memo(({ paymentProofUrl, onApprove, onRequestResend }) => {
  const handleRequestResend = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRequestResend?.();
    },
    [onRequestResend],
  );

  const handleApprove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onApprove?.();
    },
    [onApprove],
  );

  const proofStyle = useMemo(
    () => ({
      backgroundImage: paymentProofUrl ? `url(${paymentProofUrl})` : "none",
    }),
    [paymentProofUrl],
  );

  return (
    <>
      <div
        className="mt-3 w-full h-[324px] rounded-2xl border border-Color-Stroke-neutral overflow-hidden bg-center bg-cover"
        style={proofStyle}
      >
        {!paymentProofUrl && (
          <div className="w-full h-full flex items-center justify-center text-Color-Text-neutral-supplement text-sm">
            Không có ảnh minh chứng
          </div>
        )}
      </div>

      {(onRequestResend || onApprove) && (
        <div
          className={cn(
            "mt-3 w-full",
            onRequestResend && onApprove
              ? "flex items-center gap-4"
              : "flex items-center justify-center",
          )}
        >
          {onRequestResend && (
            <Button
              size="sm"
              variant="tertiary"
              className={cn(
                "rounded-xl",
                onApprove ? "flex-1" : "w-full max-w-[220px]",
              )}
              onClick={handleRequestResend}
            >
              Yêu cầu gửi lại
            </Button>
          )}
          {onApprove && (
            <Button
              size="sm"
              variant="primary"
              className="flex-1 rounded-xl"
              onClick={handleApprove}
            >
              Duyệt
            </Button>
          )}
        </div>
      )}
    </>
  );
});

function mapStatusText(
  variant: RegistrationCardVariant,
  status: RegistrationCardData["status"],
): { text: string; color: "yellow" | "green" | "red" } {
  if (variant === "fee") {
    if (status === "PENDING_PAYMENT")
      return { text: "Chờ duyệt", color: "yellow" };
    if (status === "PAYMENT_CONFIRMED")
      return { text: "Đã duyệt", color: "green" };
    return { text: "Chờ duyệt", color: "yellow" };
  }
  // info
  if (status === "PENDING_APPROVAL")
    return { text: "Chờ duyệt", color: "yellow" };
  if (status === "REJECTED") return { text: "Từ chối", color: "red" };
  if (status === "CANCELLED_BY_PLAYER")
    return { text: "Bị hủy bởi người chơi", color: "red" };
  return { text: "Đã duyệt", color: "green" };
}

const RegistrationCard: React.FC<RegistrationCardData> = ({
  playerName,
  playerFullName,
  playerAvatar,
  registrationDate,
  registrationTime,
  status,
  variant = "info",
  sequenceNumber,
  age,
  paymentProofUrl,
  resendRequested,
  onClick,
  onApprove,
  onReject,
  onRequestResend,
}) => {
  const statusInfo = useMemo(() => {
    if (
      variant === "fee" &&
      resendRequested &&
      status !== "PAYMENT_CONFIRMED"
    ) {
      return { text: "Yêu cầu gửi lại minh chứng", color: "yellow" as const };
    }
    return mapStatusText(variant, status);
  }, [variant, resendRequested, status]);

  const isFeeVariant = variant === "fee";
  const isApproved =
    (variant === "info" &&
      (status === "PROFILE_APPROVED" || status === "PENDING_PAYMENT")) ||
    (variant === "fee" && status === "PAYMENT_CONFIRMED");
  const isRejected =
    variant === "info" &&
    (status === "REJECTED" || status === "CANCELLED_BY_PLAYER");
  const timestampClassName = getTimestampClassName(isApproved, isRejected);
  const hasAvatar = playerAvatar && playerAvatar.trim() !== "";

  const headerContent = (
    <>
      <div className="flex items-center justify-between w-full">
        <div className="inline-flex items-center gap-2">
          {getTimestampIcon(variant, status)}
          <div
            className={cn(
              "inline-flex items-center gap-1 text-[10px]",
              timestampClassName,
            )}
          >
            <span>{registrationDate}</span>
            {registrationTime ? (
              <>
                <span>-</span>
                <span>{registrationTime}</span>
              </>
            ) : null}
          </div>
        </div>
        <StatusChip text={statusInfo.text} color={statusInfo.color} />
      </div>

      <div className="flex items-center gap-2 mt-3 w-[166px]">
        <Avatar className="w-10 h-10">
          {hasAvatar ? (
            <AvatarImage
              src={playerAvatar}
              alt={playerName}
              className="object-cover"
            />
          ) : null}
          <AvatarFallback className="bg-Color-Background-neutral-surface flex items-center justify-center border-none p-0 overflow-hidden">
            <DefaultAvatarFilled className="w-full h-full text-Color-Icon-unselected" />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col justify-center">
          <p className="text-[14px] leading-[22px] text-Color-Text-neutral-black">
            {playerName}
          </p>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "w-full max-w-[370px] mx-auto mb-4 border border-Color-Stroke-neutral rounded-2xl bg-Color-Background-neutral-default_primary",
        "flex flex-col",
        isFeeVariant ? "h-[480px]" : "h-auto",
      )}
      style={{ padding: "15px" }}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex flex-col text-left w-full hover:opacity-80"
          aria-label={playerName}
        >
          {headerContent}
        </button>
      ) : (
        <div className="flex flex-col w-full">{headerContent}</div>
      )}

      {variant === "info" ? (
        <InfoSection
          sequenceNumber={sequenceNumber}
          playerName={playerName}
          playerFullName={playerFullName}
          age={age}
          onApprove={onApprove}
          onReject={onReject}
        />
      ) : (
        <FeeSection
          paymentProofUrl={paymentProofUrl}
          onApprove={onApprove}
          onRequestResend={onRequestResend}
        />
      )}
    </div>
  );
};

export default memo(RegistrationCard);
